import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { CalendarCheck, BookOpen, ConciergeBell, Megaphone, Users, ArrowUpRight, AlertCircle, CheckCircle2, Circle, X, PartyPopper } from "lucide-react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";

const CONFETTI_COLORS = ["#C5A880", "#1F3A34", "#E7C9A0", "#4C7A6E", "#F2E4CF"];
function Confetti() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {Array.from({ length: 36 }).map((_, i) => (
        <span
          key={i}
          className="confetti-piece"
          style={{
            left: `${(i * 2.8) % 100}%`,
            background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
            animationDelay: `${(i % 9) * 0.08}s`,
          }}
        />
      ))}
    </div>
  );
}

const statusColor = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-700",
};

export default function Overview() {
  const [stats, setStats] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [todayCh, setTodayCh] = useState(null);
  const { features } = useOutletContext() || {};
  const channelsOn = !!features?.channels;

  useEffect(() => {
    api.get("/business/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/business/onboarding").then((r) => setOnboarding(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!channelsOn) { return; }
    api.get("/channel-bookings/today").then((r) => setTodayCh(r.data)).catch(() => {});
  }, [channelsOn]);

  const dismissOnboarding = async () => {
    setOnboarding((o) => ({ ...o, dismissed: true }));
    try { await api.post("/business/onboarding/dismiss"); } catch { /* noop */ }
  };

  const cards = stats
    ? [
        { label: "Total bookings", value: stats.bookings, icon: CalendarCheck, to: "/dashboard/bookings" },
        { label: "Pending requests", value: stats.pending_bookings, icon: AlertCircle, to: "/dashboard/bookings" },
        { label: "Directory items", value: stats.directory_items, icon: BookOpen, to: "/dashboard/directory" },
        { label: "Services", value: stats.services, icon: ConciergeBell, to: "/dashboard/services" },
        { label: "Announcements", value: stats.announcements, icon: Megaphone, to: "/dashboard/announcements" },
        { label: "Guest accounts", value: stats.guests, icon: Users, to: "/dashboard/bookings" },
      ]
    : [];

  return (
    <div className="space-y-8" data-testid="overview-page">
      <div>
        <h1 className="text-3xl text-foreground mb-1">Welcome back</h1>
        <p className="text-muted-foreground text-sm">Here's what's happening across your guest relations hub.</p>
      </div>

      {onboarding && !onboarding.complete && !onboarding.dismissed && (
        <div className="bg-card border border-border rounded-md p-5" data-testid="onboarding-checklist">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-serif text-lg text-foreground">Get your hub live</h3>
            <button onClick={dismissOnboarding} className="text-muted-foreground hover:text-foreground" title="Dismiss" data-testid="onboarding-dismiss"><X className="w-4 h-4" /></button>
          </div>
          <p className="text-sm text-muted-foreground mb-4">{onboarding.done} of {onboarding.total} done — finish these to start delighting guests.</p>
          <div className="h-1.5 bg-secondary rounded-full mb-4 overflow-hidden"><div className="h-full bg-accent transition-all" style={{ width: `${(onboarding.done / onboarding.total) * 100}%` }} /></div>
          <div className="space-y-1">
            {onboarding.items.map((it) => (
              <Link key={it.key} to={it.route} className="flex items-center gap-3 p-2 rounded hover:bg-secondary/50" data-testid={`onboarding-item-${it.key}`}>
                {it.done ? <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" /> : <Circle className="w-5 h-5 text-muted-foreground shrink-0" />}
                <span className={`text-sm ${it.done ? "text-muted-foreground line-through" : "text-foreground"}`}>{it.label}</span>
                {!it.done && <ArrowUpRight className="w-4 h-4 text-accent ml-auto" />}
              </Link>
            ))}
          </div>
        </div>
      )}

      {onboarding && onboarding.complete && !onboarding.dismissed && (
        <div className="relative overflow-hidden bg-ink text-cream rounded-md p-6" data-testid="onboarding-celebration">
          <Confetti />
          <div className="relative flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <PartyPopper className="w-8 h-8 text-gold shrink-0" strokeWidth={1.5} />
              <div>
                <h3 className="font-serif text-xl mb-1">You're live! 🎉</h3>
                <p className="text-cream-muted text-sm">Every setup step is done — your hub is ready to delight guests. Share your QR code and watch the requests roll in.</p>
                <Link to="/dashboard/qr" className="inline-flex items-center gap-1.5 text-gold text-sm mt-3 hover:underline" data-testid="celebration-qr-link">Get your QR kit <ArrowUpRight className="w-4 h-4" /></Link>
              </div>
            </div>
            <button onClick={dismissOnboarding} className="text-cream-muted hover:text-cream shrink-0" title="Dismiss" data-testid="celebration-dismiss"><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {todayCh && (todayCh.arrivals.length > 0 || todayCh.departures.length > 0) && (
        <div className="bg-card border border-border rounded-md p-5" data-testid="overview-today-channels">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-serif text-lg text-foreground">Today from channels</h3>
            <Link to="/dashboard/channels" className="text-xs text-accent hover:underline">View all</Link>
          </div>
          <p className="text-sm text-muted-foreground">{todayCh.arrivals.length} arrival(s) · {todayCh.departures.length} departure(s) across Booking.com, Airbnb and other channels.</p>
        </div>
      )}

      {stats && stats.subscription_status !== "active" && (
        <Link to="/dashboard/billing" className="block bg-ink text-cream rounded-md p-5 hover-lift" data-testid="upgrade-banner">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gold uppercase tracking-widest text-xs mb-1">No active plan</p>
              <p className="font-serif text-lg">Activate a subscription to unlock your hub's full potential.</p>
            </div>
            <ArrowUpRight className="w-5 h-5 text-gold" />
          </div>
        </Link>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <Link key={c.label} to={c.to} className="bg-card border border-border rounded-md p-5 hover-lift" data-testid={`stat-${c.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
            <c.icon className="w-5 h-5 text-accent mb-4" strokeWidth={1.5} />
            <div className="text-3xl font-serif text-foreground">{c.value}</div>
            <div className="text-sm text-muted-foreground mt-1">{c.label}</div>
          </Link>
        ))}
      </div>

      <div className="bg-card border border-border rounded-md">
        <div className="p-5 border-b border-border">
          <h3 className="font-serif text-lg text-foreground">Recent booking requests</h3>
        </div>
        <div className="divide-y divide-border">
          {stats?.recent_bookings?.length ? (
            stats.recent_bookings.map((b) => (
              <div key={b.id} className="p-5 flex items-center justify-between">
                <div>
                  <p className="text-foreground text-sm font-medium">{b.service_name}</p>
                  <p className="text-muted-foreground text-xs mt-0.5">{b.guest_name} · Room {b.room || "—"}</p>
                </div>
                <Badge className={`${statusColor[b.status] || "bg-secondary"} border-0 capitalize`}>{b.status}</Badge>
              </div>
            ))
          ) : (
            <div className="p-8 text-center text-sm text-muted-foreground">No booking requests yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
